import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '@/app/hooks';
import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { useAddClinicTransferEmbryosMutation } from '@/app/services/excel';
import { addToast } from '@/app/slices/toast';
import { DonorFertilizationLink, WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { PrimaryContext } from '@/services';

import { gradingColumnDefs } from './columnDefs';
import { GradingRow, MatingStats } from './types';

import './PseDay7Grading.css';

type PseDay7GradingProps = {
  gradeTime: number;
  graderId: string;
  discards: number;
  degenerates: number;
  setGradeTime: (value: number) => void;
  setGraderId: (value: string) => void;
  setDiscards: (value: number) => void;
  setDegenerates: (value: number) => void;
  matingStats: MatingStats;
};

export const PseDay7Grading = ({
  gradeTime,
  graderId,
  discards,
  degenerates,
  setGradeTime,
  setGraderId,
  setDiscards,
  setDegenerates,
  matingStats,
}: PseDay7GradingProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useAppDispatch();
  const { themeMode } = useContext(PrimaryContext);
  const [params, setParams] = useSearchParams();
  const beginDate = params.get('beginDate');

  const siteId = params.get('site');
  const fertilizationId = params.get('fertId');
  const donorName = params.get('donorName');

  const [isAddEmbryosLoading, setIsAddEmbryosLoading] = useState(false);
  const [rowData, setRowData] = useState<GradingRow[]>([
    {
      row: 1,
      g4: null,
      g5: null,
      g6: null,
      g7: null,
      g8: null,
      g9: null,
      total: 0,
    },
    {
      row: 2,
      g4: null,
      g5: null,
      g6: null,
      g7: null,
      g8: null,
      g9: null,
      total: 0,
    },
    {
      row: 3,
      g4: null,
      g5: null,
      g6: null,
      g7: null,
      g8: null,
      g9: null,
      total: 0,
    },
  ]);

  const [addClinicTransferEmbryos] = useAddClinicTransferEmbryosMutation();

  const { data: techsData, isLoading } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
    take: 100,
  });

  const memoizedOptions = useMemo(() => techsData || [], [techsData]);

  useEffect(() => {
    setRowData([
      {
        row: 1,
        g4: null,
        g5: null,
        g6: null,
        g7: null,
        g8: null,
        g9: null,
        total: 0,
      },
      {
        row: 2,
        g4: null,
        g5: null,
        g6: null,
        g7: null,
        g8: null,
        g9: null,
        total: 0,
      },
      {
        row: 3,
        g4: null,
        g5: null,
        g6: null,
        g7: null,
        g8: null,
        g9: null,
        total: 0,
      },
    ]);
    setDiscards(0);
    setDegenerates(0);
  }, [fertilizationId]);

  const handleAddEmbryos = useCallback(async () => {
    setIsAddEmbryosLoading(true);
    try {
      const gradeValues: { key: string; value: number }[] = [];
      const transferDay = beginDate
        ? new Date(new Date(beginDate).getTime() + 7 * 24 * 60 * 60 * 1000)
        : null;

      rowData.forEach((row) => {
        if ((row.g9 || 0) > 0)
          gradeValues.push({ key: `9${row.row}`, value: Number(row.g9) || 0 });
        if ((row.g8 || 0) > 0)
          gradeValues.push({ key: `8${row.row}`, value: Number(row.g8) || 0 });
        if ((row.g7 || 0) > 0)
          gradeValues.push({ key: `7${row.row}`, value: Number(row.g7) || 0 });
        if ((row.g6 || 0) > 0)
          gradeValues.push({ key: `6${row.row}`, value: Number(row.g6) || 0 });
        if ((row.g5 || 0) > 0)
          gradeValues.push({ key: `5${row.row}`, value: Number(row.g5) || 0 });
        if ((row.g4 || 0) > 0)
          gradeValues.push({ key: `4${row.row}`, value: Number(row.g4) || 0 });
      });

      const response = await addClinicTransferEmbryos({
        input: {
          fertilizationId: fertilizationId,
          graderId: graderId,
          gradeTime: gradeTime.toString(),
          transferDay,
          transferSiteId: siteId,
          totalDiscarded: discards,
          totalDegenerates: degenerates,
          gradeValues,
          clinicRanchHaulin: null,
        },
      }).unwrap();

      if (response?.addClinicTransferEmbryos?.code === 500) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Server Error',
            detail: response?.addClinicTransferEmbryos?.message,
            life: 3000,
          }),
        );
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Success',
            detail: response?.addClinicTransferEmbryos?.message,
            life: 3000,
          }),
        );
      }
    } finally {
      setIsAddEmbryosLoading(false);
    }
  }, [
    rowData,
    gradeTime,
    graderId,
    discards,
    degenerates,
    addClinicTransferEmbryos,
  ]);

  return (
    <div className="pseDay7-grading">
      <div className="pse-grading-grid">
        <WithLocalStorage storageKey="pseDay7-grid">
          <AgGridReact
            gridOptions={defaultGridOptions}
            className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
            defaultColDef={{
              resizable: true,
              sortable: false,
              filter: false,
              cellStyle: { textAlign: 'center' },
            }}
            columnDefs={gradingColumnDefs}
            rowData={rowData}
            onCellValueChanged={(params) => {
              const updatedData = rowData.map((row) =>
                row === params.oldValue
                  ? { ...row, [params.column.getId()]: params.newValue }
                  : row,
              );
              setRowData(updatedData);
            }}
          />
        </WithLocalStorage>
      </div>
      <div
        className="form-controls"
        style={{ marginBottom: '1rem', display: 'grid', gap: '0.5rem' }}
      >
        <div className="p-field">
          <label htmlFor="gradeTime" className="p-d-block">
            Load Day
          </label>
          <Dropdown
            id="gradeTime"
            value={gradeTime}
            options={[
              { label: '6.5', value: 6.5 },
              { label: '7', value: 7 },
            ]}
            onChange={(e) => setGradeTime(e.value)}
            className="p-inputtext-sm"
          />
        </div>

        <div className="p-field">
          <label htmlFor="graderId" className="p-d-block">
            Embryologist
          </label>
          <Dropdown
            id="graderId"
            value={graderId}
            options={memoizedOptions}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setGraderId(e.value)}
            placeholder="Select Embryologist"
            loading={isLoading}
            filter
          />
        </div>

        <div className="p-field">
          <label htmlFor="discards" className="p-d-block">
            Discards
          </label>
          <InputNumber
            id="discards"
            value={discards}
            onValueChange={(e) => setDiscards(e.target.value || 0)}
            className="p-inputtext-sm"
            min={0}
          />
        </div>

        <div className="p-field">
          <label htmlFor="degenerates" className="p-d-block">
            Degenerates
          </label>
          <InputNumber
            id="degenerates"
            value={degenerates}
            onValueChange={(e) => setDegenerates(e.target.value || 0)}
            className="p-inputtext-sm"
            min={0}
          />
        </div>
      </div>
      <div className="grading-actions" style={{ marginTop: '.833rem' }}>
        <Button
          label="Add Embryos to OvaDrive"
          onClick={handleAddEmbryos}
          disabled={!graderId || !fertilizationId}
          className="p-button-sm"
          loading={isAddEmbryosLoading}
        />
      </div>
      <DonorFertilizationLink
        fertilizationId={fertilizationId as string}
        donorName={donorName as string}
      />
      <div className="mating-stats">
        <div className="stat-item">
          <span className="stat-label">Trf:</span>
          <span className="stat-value">{matingStats.trf}</span>
        </div>

        <div className="stat-item">
          <span className="stat-label">G1:</span>
          <span className="stat-value">{matingStats.g1}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">G2:</span>
          <span className="stat-value">{matingStats.g2}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">G3:</span>
          <span className="stat-value">{matingStats.g3}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Frz:</span>
          <span className="stat-value">{matingStats.frz}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">DCG1:</span>
          <span className="stat-value">{matingStats.dcg1}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">DCG2:</span>
          <span className="stat-value">{matingStats.dcg2}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">DCG3:</span>
          <span className="stat-value">{matingStats.dcg3}</span>
        </div>
      </div>
    </div>
  );
};
